import {
  BaseGenerationJobHandler,
  ensureUniqueTitle,
  generateMarkdownWithFrontmatter,
} from "@brains/plugins";
import { GENERATE_CHANNELS, NEWSLETTER_CHANNELS } from "@brains/contracts";
import type { GeneratedContent } from "@brains/plugins";
import type { Logger } from "@brains/utils/logger";
import type { ProgressReporter } from "@brains/utils/progress";
import { slugify } from "@brains/utils/string-utils";
import { z } from "@brains/utils/zod";
import { type GenerationResult } from "@brains/contracts";
import { baseEntitySchema, type EntityPluginContext } from "@brains/plugins";
import { fetchVoiceGuidance } from "@brains/contracts";
import type { NewsletterMetadata } from "../schemas/newsletter";

/** Source entity shape consumed by newsletter generation */
const sourceEntitySchema = baseEntitySchema.extend({
  metadata: z.looseObject({
    title: z.string(),
    slug: z.string(),
    status: z.string(),
    excerpt: z.string().optional(),
  }),
});
type SourceEntity = z.output<typeof sourceEntitySchema>;

/**
 * Input schema for newsletter generation job
 */
/** Shape the newsletter generation template returns. */
export const generatedNewsletterSchema: z.ZodObject<{
  subject: z.ZodString;
  content: z.ZodString;
}> = z.object({ subject: z.string(), content: z.string() });

export const generationJobSchema: z.ZodObject<{
  prompt: z.ZodOptional<z.ZodString>;
  sourceEntityIds: z.ZodOptional<z.ZodArray<z.ZodString>>;
  sourceEntityType: z.ZodOptional<z.ZodEnum<{ post: "post" }>>;
  content: z.ZodOptional<z.ZodString>;
  subject: z.ZodOptional<z.ZodString>;
  addToQueue: z.ZodOptional<z.ZodBoolean>;
}> = z.object({
  prompt: z.string().optional().describe("AI generation prompt"),
  sourceEntityIds: z
    .array(z.string())
    .optional()
    .describe("Entity IDs to include in newsletter (e.g., blog posts)"),
  sourceEntityType: z
    .enum(["post"])
    .optional()
    .describe("Type of source entities"),
  content: z.string().optional().describe("Direct content (skip AI)"),
  subject: z
    .string()
    .optional()
    .describe("Newsletter subject (AI-generated if not provided)"),
  addToQueue: z
    .boolean()
    .optional()
    .describe("Create as queued (true) or draft (false)"),
});

export type GenerationJobData = z.output<typeof generationJobSchema>;

/**
 * Job handler for newsletter generation
 * Handles AI-powered content generation from prompts or source entities
 */
export class GenerationJobHandler extends BaseGenerationJobHandler<
  GenerationJobData,
  GenerationResult
> {
  constructor(logger: Logger, context: EntityPluginContext) {
    super(logger, context, {
      schema: generationJobSchema,
      jobTypeName: NEWSLETTER_CHANNELS.generation,
      entityType: "newsletter",
    });
  }

  protected async generate(
    data: GenerationJobData,
    progressReporter: ProgressReporter,
  ): Promise<GeneratedContent> {
    const addToQueue = data.addToQueue ?? false;
    const { prompt, sourceEntityIds, sourceEntityType } = data;
    let { content, subject } = data;

    // Case 1: Direct content provided
    if (content) {
      if (!subject) {
        this.failEarly("Subject is required when providing content directly");
      }
      await this.reportProgress(progressReporter, {
        progress: 50,
        message: "Using provided content",
      });
    }
    // Case 2: Generate from source entities (blog posts)
    else if (sourceEntityIds && sourceEntityIds.length > 0) {
      const entityType = sourceEntityType ?? "post";

      await this.reportProgress(progressReporter, {
        progress: 10,
        message: `Fetching ${sourceEntityIds.length} source entities`,
      });

      const results = await Promise.all(
        sourceEntityIds.map((id) =>
          this.context.entityService.getEntity(
            {
              entityType,
              id,
            },
            sourceEntitySchema,
          ),
        ),
      );
      const posts = results.filter((e): e is SourceEntity => e != null);

      if (posts.length === 0) {
        this.failEarly(
          `No source entities found for IDs: ${sourceEntityIds.join(", ")}`,
        );
      }

      await this.reportProgress(progressReporter, {
        progress: 30,
        message: `Generating newsletter from ${posts.length} posts`,
      });

      const postSummaries = posts
        .map(
          (p) =>
            `## ${p.metadata.title}\n\n${p.metadata.excerpt ?? p.content.slice(0, 500)}`,
        )
        .join("\n\n---\n\n");

      const baseInstructions = `Write a newsletter that presents these blog posts:

${postSummaries}

Summarize each post and link to it.`;

      const finalPrompt = prompt
        ? `${baseInstructions}\n\nAdditional instructions: ${prompt}`
        : baseInstructions;

      const voiceGuidance = await fetchVoiceGuidance(
        this.context.entityService,
      );
      const generated = await this.context.ai.generate(
        {
          prompt: finalPrompt,
          templateName: NEWSLETTER_CHANNELS.generation,
          representedIdentity: "anchor",
          ...(voiceGuidance && { styleGuide: { voice: voiceGuidance } }),
        },
        generatedNewsletterSchema,
      );

      subject = subject ?? generated.subject;
      content = generated.content;

      await this.reportProgress(progressReporter, {
        progress: 50,
        message: "Newsletter generated from posts",
      });
    }
    // Case 3: Generate from prompt
    else if (prompt) {
      await this.reportProgress(progressReporter, {
        progress: 10,
        message: "Generating newsletter with AI",
      });

      const voiceGuidance = await fetchVoiceGuidance(
        this.context.entityService,
      );
      const generated = await this.context.ai.generate(
        {
          prompt,
          templateName: NEWSLETTER_CHANNELS.generation,
          representedIdentity: "anchor",
          ...(voiceGuidance && { styleGuide: { voice: voiceGuidance } }),
        },
        generatedNewsletterSchema,
      );

      subject = subject ?? generated.subject;
      content = generated.content;

      await this.reportProgress(progressReporter, {
        progress: 50,
        message: "Newsletter generated",
      });
    } else {
      this.failEarly(
        "No content source provided (prompt, sourceEntityIds, or content)",
      );
    }

    if (!content || !subject) {
      this.failEarly("Content or subject was not generated");
    }

    const status = addToQueue ? "queued" : "draft";

    const metadata: NewsletterMetadata = {
      subject,
      status,
      ...(sourceEntityIds && { entityIds: sourceEntityIds }),
      ...(sourceEntityType && { sourceEntityType }),
    };

    const finalSubject = await ensureUniqueTitle({
      entityType: "newsletter",
      title: subject,
      deriveId: slugify,
      regeneratePrompt:
        "Generate a different newsletter subject line on the same topic.",
      context: this.context,
    });

    if (finalSubject !== subject) {
      metadata.subject = finalSubject;
    }

    const markdownContent = generateMarkdownWithFrontmatter(content, metadata);

    return {
      id: slugify(finalSubject),
      content: markdownContent,
      metadata,
      title: finalSubject,
      createOptions: { deduplicateId: true },
    };
  }

  protected override async onGenerationFailure(
    _data: GenerationJobData,
    error: string,
  ): Promise<void> {
    await this.context.messaging.send({
      type: GENERATE_CHANNELS.reportFailure,
      payload: {
        entityType: "newsletter",
        error,
      },
    });
  }

  protected override async afterCreate(
    _data: GenerationJobData,
    entityId: string,
    _progressReporter: ProgressReporter,
    _generated: GeneratedContent,
  ): Promise<void> {
    await this.context.messaging.send({
      type: GENERATE_CHANNELS.reportSuccess,
      payload: {
        entityType: "newsletter",
        entityId,
      },
    });
  }

  protected override summarizeDataForLog(
    data: GenerationJobData,
  ): Record<string, unknown> {
    return {
      hasPrompt: !!data.prompt,
      sourceEntityCount: data.sourceEntityIds?.length ?? 0,
      sourceEntityType: data.sourceEntityType,
      addToQueue: data.addToQueue ?? false,
    };
  }
}
